import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Tabs,
  Tab,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Layout from '../components/Layout';
import AdminUserManagement from '../components/AdminUserManagement';
import PropTrakUserManagement from '../components/PropTrakUserManagement';
import api from '../services/api';

const PageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  minHeight: '100vh',
  backgroundColor: '#F7FAFC',
}));

const MainContent = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  marginLeft: 280, // Width of main sidebar (80px) + people submenu (200px)
}));

const Header = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
}));

const GlobalAuditTrail = () => {
    const [logs, setLogs] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    React.useEffect(() => {
        const fetchLogs = async () => {
            try {
                // Assuming we have a global audit endpoint or we can fetch recent
                // For now, let's assume we might need to add this endpoint or reuse company logs?
                // The prompt asked for "global audit trail in the manage users section".
                // Usually this means ALL adjustments across ALL companies.
                // We'll need a new backend endpoint for this.
                // Placeholder:
                const response = await api.request<any[]>('/api/proptrak/audit-logs/global'); 
                setLogs(response);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchLogs();
    }, []);

    return (
        <Card>
            <CardContent>
                <Typography variant="h6" gutterBottom>Global Usage Limit Audit Trail</Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Company</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Cost</TableCell>
                        <TableCell>Performed By</TableCell>
                        <TableCell>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {logs.length > 0 ? (
                        logs.map((log) => (
                          <TableRow key={log.id}>
                            <TableCell>{new Date(log.created_at).toLocaleString()}</TableCell>
                            <TableCell>{log.company_name}</TableCell>
                            <TableCell style={{ textTransform: 'capitalize' }}>{log.limit_type}</TableCell>
                            <TableCell>
                                {log.limit_type === 'storage' 
                                    ? `+${(log.adjustment_amount / (1024*1024*1024)).toFixed(1)} GB` 
                                    : `+${log.adjustment_amount}`}
                            </TableCell>
                            <TableCell>
                                {log.is_free ? (
                                    <Chip label="Free" color="success" size="small" variant="outlined" />
                                ) : (
                                    `$${log.cost_amount?.toFixed(2)}`
                                )}
                            </TableCell>
                            <TableCell>{log.performed_by}</TableCell>
                            <TableCell>{log.reason}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                              {loading ? "Loading..." : "No audit logs found."}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
            </CardContent>
        </Card>
    );
};

const UsersPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(() => {
    // Try to get the active tab from localStorage, default to 0 (PropTrak Users)
    const savedTab = localStorage.getItem('usersPageActiveTab');
    return savedTab ? parseInt(savedTab) : 0;
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    // Save the active tab to localStorage
    localStorage.setItem('usersPageActiveTab', newValue.toString());
  };

  return (
    <PageContainer>
      <Layout>
        <MainContent>
          <Header>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1A202C' }}>
              Users
            </Typography>
          </Header>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange}
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1rem',
                  minWidth: 120,
                },
                '& .Mui-selected': {
                  color: '#6B46C1',
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#6B46C1',
                },
              }}
            >
              <Tab label="PropTrak Users" />
              <Tab label="Admin Users" />
              <Tab label="Global Audit Trail" />
            </Tabs>
          </Box>

          {/* Tab Content */}
          {activeTab === 0 && (
            <PropTrakUserManagement />
          )}

          {activeTab === 1 && (
            <AdminUserManagement />
          )}

          {activeTab === 2 && (
            <GlobalAuditTrail />
          )}
        </MainContent>
      </Layout>
    </PageContainer>
  );
};

export default UsersPage; 